import React, { useState, useContext, useEffect, useImperativeHandle, useRef } from "react";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { AccountContext } from "context/account";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTagInput from "components/SuiTagInput";
import Tooltip from "@mui/material/Tooltip";

import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

import toast from "react-hot-toast";

import CategoryContext from "context/category-context";

import api from "services/api";
import moment from "moment";

import { sizeOptions, seasonOptions } from "utils/data";

const Color = require("color");

const ImportColorModal = React.forwardRef(
  (
    {
      visible,
      handleClose,
      handleOk,
      color = {},
      handleRefresh = () => {},
      handleAfterOk = () => {},
    },
    ref
  ) => {
    const [data, setData] = useState([]);
    const [filteredColors, setFilteredColors] = useState([]);

    const [selectedColor, setSelectedColor] = useState({});

    useEffect(() => {
      if (color) {
        setSelectedColor(color);
      }
      retrievePalette();
    }, []);

    const retrievePalette = async () => {
      try {
        const { data } = await api.palette.list({
          order: "-hex",
        });
        setData(data?.colors || []);
        setFilteredColors(data?.colors || []);
      } catch (err) {
        toast.error(`Retrieve color list failed!`);
        console.log(err);
      }
    };

    const handleSearchChange = (value) => {
      if (value) {
        let filtered = data?.filter((x) => x.name.toLowerCase().includes(value.toLowerCase()));
        setFilteredColors(filtered);
      } else {
        setFilteredColors(data);
      }
    };

    return (
      <Dialog
        open={visible}
        onClose={(e, reason) => {
          if (reason != "backdropClick") {
            handleClose();
          }
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="md"
        fullWidth={true}
      >
        <DialogTitle id="alert-dialog-title">
          {"Select a color from color library"}

          <IconButton
            aria-label="close"
            onClick={() => handleClose()}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <DialogContentText id="alert-dialog-description" style={{ maxHeight: 600 }}>
            <SuiBox display="flex" justifyContent="flex-end">
              <SuiBox sx={{ mb: 1, px: 2, maxWidth: 400 }}>
                <SuiInput
                  placeholder="Search Color Name"
                  type="text"
                  onChange={(e) => handleSearchChange(e?.target?.value)}
                  icon={{
                    direction: "right",
                    component: <SearchIcon sx={{ cursor: "pointer" }} />,
                  }}
                />{" "}
              </SuiBox>
            </SuiBox>

            {filteredColors.map((x, index) => (
              <SuiBox key={index} display={"inline-block"} mr={1} sx={{ textAlign: "center" }}>
                <SuiBox
                  sx={{
                    cursor: "pointer",
                    width: 80,
                    height: 40,
                    backgroundColor: x?.hex || "#000000",
                    border:
                      selectedColor?.id == x.id
                        ? `ridge 3px  #21d4fd`
                        : `solid 3px ${x?.hex || "#000000"}`,
                  }}
                  onClick={() => setSelectedColor(x)}
                />
                <Tooltip title={x?.name}>
                  <SuiTypography
                    sx={{
                      wordBreak: "break-word",
                      maxWidth: 76,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: "1",
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {x?.name}
                  </SuiTypography>
                </Tooltip>
              </SuiBox>
            ))}

            <SuiBox
              pt={3}
              px={3}
              display={"flex"}
              justifyContent="end"
              alignItems={{ xs: "flex-start", sm: "center" }}
              flexDirection={{ xs: "column", sm: "row" }}
            >
              <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                  <SuiButton variant="gradient" size="medium" onClick={handleClose} color="light">
                    Cancel
                  </SuiButton>
                </SuiBox>
              </SuiBox>
              <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                  <SuiButton
                    size="medium"
                    variant="gradient"
                    color="dark"
                    disabled={selectedColor?.id ? false : true}
                    onClick={() => handleOk(selectedColor)}
                  >
                    Import
                  </SuiButton>
                </SuiBox>
              </SuiBox>
            </SuiBox>
          </DialogContentText>
        </DialogContent>
      </Dialog>
    );
  }
);

export default ImportColorModal;
